import {Component, OnInit} from '@angular/core';
import { subDays } from 'date-fns';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {CaregiverService, ModalService, UserService, ValidateService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {Location} from '@angular/common';
import {environment} from '../../../../environments/environment';
import { readPage, toApiInstant } from '../../alarms-api';
import { normalizeImeiChangeRow } from '../../reports-api';

/**
 * Ile wierszy bierzemy na jedno żądanie przy eksporcie. Eksport dotyczy CAŁEGO
 * wybranego zakresu dat, a nie strony, więc dociągamy kolejne porcje aż do końca —
 * 200 to maksimum przyjmowane przez API.
 */
const EXPORT_BATCH = 200;

@Component({
  selector: 'app-pop-up-reports',
  templateUrl: './pop-up-reports.component.html',
  styleUrls: ['./pop-up-reports.component.scss']
})
export class PopUpReportsComponent implements OnInit  {

  timeRangeOptions = [];

  // Nie zaczynamy od `true`: modal nie pobiera już nic przy otwarciu, więc przycisk
  // „Pobierz" nie ma na co czekać. Flaga gasi go tylko na czas samego pobierania.
  public isLoading = false;
  public specialClientForm: FormGroup;
  public allPatientsForm: FormGroup;
  public allPatientsShortForm: FormGroup;
  public currentUser;
  reportType: string;
  format: 'xlsx' | 'pdf' = 'xlsx';

  public chosenOptionReportType;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private caregiverService: CaregiverService,
    private translateService: TranslateService,
    private userService: UserService,
    public location: Location,
    private modalService: ModalService,
    private validateService: ValidateService
  ) {
    this.specialClientForm = fb.group({
      'dateFrom': '',
      'dateTo': ''
    }, { validator: this.validateService.checkDateRange });

    this.allPatientsForm = fb.group({
      'dateFrom': ['', [Validators.required]],
      'dateTo': ['', [Validators.required]]
    }, { validator: this.validateService.checkDateRange });

    this.allPatientsShortForm = fb.group({
      'dateFrom': ['', [Validators.required]],
      'dateTo': ['', [Validators.required]]
    }, { validator: this.validateService.checkDateRange });
  }

  ngOnInit() {
    this.timeRangeOptions = [
      { id: '24h', label: this.translateService.instant('COMMON.LAST_24H'), selected: true },
      { id: '7days', label: this.translateService.instant('COMMON.LAST_7_DAYS'), selected: false },
      { id: 'month', label: this.translateService.instant('COMMON.THIS_MONTH'), selected: false },
      { id: 'custom', label: this.translateService.instant('COMMON.CUSTOM_RANGE'), selected: false }
    ];

    this.selectTimeRange('24h');

    // Dwa żądania, które leciały tutaj przy każdym otwarciu modala, zniknęły:
    //  - getHistoryOfBand() trafiał do `historyData`, którego ten szablon nie renderuje,
    //  - getImeiChanges() ściągał CAŁĄ historię zmian, żeby przy pobieraniu odfiltrować
    //    z niej wybrany zakres dat po stronie przeglądarki.
    // Zakres dat idzie teraz do API (from/to), a listę pobieramy dopiero przy eksporcie.
    this.userService.currentUser.subscribe(
      (userData) => {
        this.currentUser = userData;
      }
    );
  }

  selectTimeRange(optionId: string): void {
    const now = new Date();
    let dateFrom: Date;
    let dateTo = new Date();

    switch (optionId) {
      case '24h':
        dateFrom = subDays(now, 1);
        break;
      case '7days':
        dateFrom = subDays(now, 7);
        break;
      case 'month':
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'custom':
        dateFrom = null;
        dateTo = null;
        break;
      default:
        return;
    }

    this.timeRangeOptions = this.timeRangeOptions.map(option => ({
      ...option,
      selected: option.id === optionId
    }));

    if (optionId !== 'custom') {
      const fromStruct = {
        year: dateFrom.getFullYear(),
        month: dateFrom.getMonth() + 1,
        day: dateFrom.getDate()
      };
      const toStruct = {
        year: dateTo.getFullYear(),
        month: dateTo.getMonth() + 1,
        day: dateTo.getDate()
      };

      this.specialClientForm.patchValue({ dateFrom: fromStruct, dateTo: toStruct });
      this.allPatientsForm.patchValue({ dateFrom: fromStruct, dateTo: toStruct });
      this.allPatientsShortForm.patchValue({ dateFrom: fromStruct, dateTo: toStruct });
    } else {
      this.specialClientForm.patchValue({ dateFrom: '', dateTo: '' });
      this.allPatientsForm.patchValue({ dateFrom: '', dateTo: '' });
      this.allPatientsShortForm.patchValue({ dateFrom: '', dateTo: '' });
    }
  }

  generateReport(type) {
    const form = this.getForm(type);
    form.controls.dateFrom.markAsTouched();
    form.controls.dateTo.markAsTouched();

    const dateFrom = form.value.dateFrom;
    const dateTo = form.value.dateTo;

    const fromInvalid = !dateFrom || !dateFrom.year || !dateFrom.month || !dateFrom.day;
    const toInvalid = !dateTo || !dateTo.year || !dateTo.month || !dateTo.day;

    if (fromInvalid) {
      form.controls.dateFrom.setErrors({ dateInvalid: true });
    }
    if (toInvalid) {
      form.controls.dateTo.setErrors({ dateInvalid: true });
    }
    if (fromInvalid || toInvalid) {
      return;
    }

    if (this.getForm(type).valid) {
      let dateFrom = new Date(this.getForm(type).value.dateFrom.year, this.getForm(type).value.dateFrom.month - 1, this.getForm(type).value.dateFrom.day);
      let dateTo = new Date(this.getForm(type).value.dateTo.year, this.getForm(type).value.dateTo.month - 1, this.getForm(type).value.dateTo.day);
      if (dateFrom.getTime() > dateTo.getTime()) {
      } else {
        this.isLoading = true;
        let lang = localStorage.getItem('lang') ? localStorage.getItem('lang') : 'pl';
        this.caregiverService.generateReport(type, this.getForm(type).value, lang, this.format).subscribe(data => {
            window.open(environment.api_url + '/patient/download-report/' + data.uniqid, '_blank');
            this.isLoading = false;
          },
          () => {
            this.modalService.open(this.translateService.instant('FORMS.NO-RECORDS'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-REPORTS.POPUP.FORMS-NO-RECORDS');
            this.isLoading = false;
          });
      }

    }
  }

  getForm(type) {
    if (type === 'specialClient') { return this.specialClientForm; }
    if (type === 'patients') { return this.allPatientsForm; }
    if (type === 'patientsShort') { return this.allPatientsShortForm; }
    if (type === 'imeiHistory') { return this.allPatientsForm; }
  }

  saveData(data, fileName) {
    const a = document.createElement('a');
    document.body.appendChild(a);

    const blob = new Blob(['\uFEFF' + data], { type: 'application/CSV;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  isCustomSelected(): boolean {
    return this.timeRangeOptions.some(option => option.id === 'custom' && option.selected);
  }

  onKeyDown(event: KeyboardEvent, optionId: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectTimeRange(optionId);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusNextOption(optionId);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusPreviousOption(optionId);
    }
  }

  private focusNextOption(currentId: string): void {
    const currentIndex = this.timeRangeOptions.findIndex(option => option.id === currentId);
    const nextIndex = (currentIndex + 1) % this.timeRangeOptions.length;
    const nextOption = this.timeRangeOptions[nextIndex];
    this.selectTimeRange(nextOption.id);
    this.focusOption(nextOption.id);
  }

  private focusPreviousOption(currentId: string): void {
    const currentIndex = this.timeRangeOptions.findIndex(option => option.id === currentId);
    const prevIndex = currentIndex === 0 ? this.timeRangeOptions.length - 1 : currentIndex - 1;
    const prevOption = this.timeRangeOptions[prevIndex];
    this.selectTimeRange(prevOption.id);
    this.focusOption(prevOption.id);
  }

  private focusOption(optionId: string): void {
    setTimeout(() => {
      const element = document.querySelector(`[role="radio"][aria-checked="true"]`) as HTMLElement;
      if (element) {
        element.focus();
      }
    });
  }

  selectedOption = '24h';

  selectOption(option: string) {
    this.selectedOption = option;
    this.updateRadioStates();
  }


  private navigateToNextOption() {
    const options = ['24h', '7days', 'month', 'custom'];
    const currentIndex = options.indexOf(this.selectedOption);
    const nextIndex = (currentIndex + 1) % options.length;
    this.selectOption(options[nextIndex]);
    this.focusOption(options[nextIndex]);
  }

  private navigateToPreviousOption() {
    const options = ['24h', '7days', 'month', 'custom'];
    const currentIndex = options.indexOf(this.selectedOption);
    const previousIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
    this.selectOption(options[previousIndex]);
    this.focusOption(options[previousIndex]);
  }

  private updateRadioStates() {
    const cards = document.querySelectorAll('.option-card');
    const options = ['24h', '7days', 'month', 'custom'];

    cards.forEach((card, index) => {
      const isSelected = options[index] === this.selectedOption;
      card.setAttribute('aria-checked', isSelected.toString());
      card.setAttribute('tabindex', isSelected ? '0' : '-1');

      if (isSelected) {
        card.classList.add('selected');
        const svg = card.querySelector('svg');
        if (svg) {
          svg.classList.remove('radio-unselected');
          svg.classList.add('radio-selected');
          const path = svg.querySelector('path');
          if (path) {
            path.setAttribute('fill-rule', 'evenodd');
            path.setAttribute('clip-rule', 'evenodd');
            path.setAttribute('d', 'M12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5 9.51472 14.4853 7.5 12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5ZM12 22.5C17.799 22.5 22.5 17.799 22.5 12C22.5 6.20101 17.799 1.5 12 1.5C6.20101 1.5 1.5 6.20101 1.5 12C1.5 17.799 6.20101 22.5 12 22.5Z');
            path.setAttribute('fill', 'black');
            path.removeAttribute('fill-opacity');
          }
        }
      } else {
        card.classList.remove('selected');
        const svg = card.querySelector('svg');
        if (svg) {
          svg.classList.remove('radio-selected');
          svg.classList.add('radio-unselected');
          const path = svg.querySelector('path');
          if (path) {
            path.removeAttribute('fill-rule');
            path.removeAttribute('clip-rule');
            path.setAttribute('d', 'M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM12 20.25C10.3683 20.25 8.77326 19.7661 7.41655 18.8596C6.05984 17.9531 5.00242 16.6646 4.378 15.1571C3.75358 13.6496 3.5902 11.9908 3.90853 10.3905C4.22685 8.79016 5.01259 7.32015 6.16637 6.16637C7.32016 5.01259 8.79017 4.22685 10.3905 3.90852C11.9909 3.59019 13.6497 3.75357 15.1571 4.37799C16.6646 5.00242 17.9531 6.05984 18.8596 7.41655C19.7661 8.77325 20.25 10.3683 20.25 12C20.2475 14.1873 19.3775 16.2843 17.8309 17.8309C16.2843 19.3775 14.1873 20.2475 12 20.25Z');
            path.setAttribute('fill', 'black');
            path.setAttribute('fill-opacity', '0.2');
          }
        }
      }
    });
  }

  onCancel() {
    this.activeModal.dismiss();
  }

  onDownload() {
    if (this.reportType === 'imeiHistory' && this.format === 'xlsx') {
      this.downloadImeiHistory();
    } else {
      this.generateReport(this.reportType);
    }
  }

  private getDateFromStruct(struct: any): Date | null {
    if (!struct || !struct.year) { return null; }
    return new Date(struct.year, struct.month - 1, struct.day);
  }

  private downloadImeiHistory() {
    const form = this.getForm('imeiHistory');

    form.controls.dateFrom.markAsTouched();
    form.controls.dateTo.markAsTouched();

    const dateFromStruct = form.value.dateFrom;
    const dateToStruct = form.value.dateTo;

    const fromInvalid = !dateFromStruct || !dateFromStruct.year || !dateFromStruct.month || !dateFromStruct.day;
    const toInvalid = !dateToStruct || !dateToStruct.year || !dateToStruct.month || !dateToStruct.day;

    if (fromInvalid) {
      form.controls.dateFrom.setErrors({ dateInvalid: true });
    }
    if (toInvalid) {
      form.controls.dateTo.setErrors({ dateInvalid: true });
    }
    if (fromInvalid || toInvalid || !form.valid) { return; }

    const dateFrom = this.getDateFromStruct(form.value.dateFrom);
    const dateTo = this.getDateFromStruct(form.value.dateTo);

    if (!dateFrom || !dateTo) { return; }

    // Koniec dnia, żeby zakres pozostał obustronnie domknięty — tak działał filtr
    // po stronie przeglądarki, którego to zapytanie jest odpowiednikiem.
    const dateToEnd = new Date(dateTo);
    dateToEnd.setHours(23, 59, 59, 999);

    this.isLoading = true;
    this.collectImeiChanges(toApiInstant(dateFrom), toApiInstant(dateToEnd), 0, []);
  }

  /**
   * Dociąga kolejne porcje zmian IMEI z wybranego zakresu, aż backend przestanie
   * zgłaszać `has_more`, i dopiero wtedy buduje plik. Zakres filtruje serwer —
   * przesianie jednej pobranej strony odpowiadałoby na inne pytanie niż zadał użytkownik.
   */
  private collectImeiChanges(from: string, to: string, offset: number, collected: any[]): void {
    this.caregiverService.findImeiChanges({ from: from, to: to, limit: EXPORT_BATCH, offset: offset }).subscribe(
      response => {
        const page = readPage(response, EXPORT_BATCH);
        const rows = collected.concat(page.items.map(item => normalizeImeiChangeRow(item)));

        // Warunek na `items.length` to bezpiecznik: gdyby backend zgłosił has_more
        // przy pustej stronie, pętla nie miałaby jak się skończyć.
        if (page.hasMore && page.items.length > 0) {
          this.collectImeiChanges(from, to, offset + page.items.length, rows);
          return;
        }

        this.saveImeiChangesCsv(rows);
        this.isLoading = false;
        this.activeModal.close();
      },
      error => {
        this.isLoading = false;
        console.error('[pop-up-reports] /reports/imei-changes: żądanie nieudane', error);
        this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-REPORTS.POPUP.BACKEND-COULDNT-GET-DATA');
      }
    );
  }

  private saveImeiChangesCsv(rows: any[]): void {
    let csv = '';
    const lang = localStorage.getItem('lang') || 'pl';
    let filename;

    if (lang === 'pl' || lang === 'it') {
      csv += this.translateService.instant('REPORT.IMEI-CHANGE-DATE') + ';' + this.translateService.instant('REPORT.OLD-IMEI') + ';' + this.translateService.instant('REPORT.NEW-IMEI') + ';' + this.translateService.instant('REPORT.CHANGED-BY') + '\n';
      filename = lang === 'pl' ? 'historia_zmiany_imei_raport' : 'modificare_il_rapporto_imei';
    } else {
      csv += 'Date of IMEI change;Previous IMEI;New IMEI;Changed by\n';
      filename = 'change_imei_report';
    }

    rows.forEach(row => {
      const date = new Date(row.changedAt);
      csv += `"${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)} ` +
        `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}:${('0' + date.getSeconds()).slice(-2)}";` +
        `"=""${row.oldImei}""";` +
        `"=""${row.newImei}""";` +
        `"${row.changedBy}"\n`;
    });

    this.saveData(csv, `${filename}.csv`);
  }

  ngAfterViewInit() {
    this.updateRadioStates();
  }

  get activeForm(): FormGroup {
    return this.getForm(this.reportType);
  }

}
